import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getErrorMessage } from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FolderOpen, Plus, TriangleAlert } from "lucide-react";

const THIS_YEAR = new Date().getFullYear();
const blankClient = { name: "", industry: "", npwp: "", is_listed: false, is_group: false, pic_name: "", pic_email: "" };
const blankEngagement = (year) => ({
  client_id: "", name: "", period: `31 Desember ${year}`, year,
  framework: "SAK / PSAK", engagement_type: "Audit Laporan Keuangan",
  applicable_standards: "SA / SPAP", methodology_version: "2026.1", eqr_required: false,
});

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [listError, setListError] = useState(null);
  const [open, setOpen] = useState(false);
  const [engOpen, setEngOpen] = useState(false);
  const [openPicker, setOpenPicker] = useState(null);      // client whose engagements are listed
  const [engagements, setEngagements] = useState([]);
  const [form, setForm] = useState(blankClient);
  const [eng, setEng] = useState(blankEngagement(THIS_YEAR));
  // Saving state does double duty: it shows the user something is happening, and it is what
  // stops a second submit creating a second engagement.
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setListError(null);
    return api.get("/clients")
      .then((r) => setClients(r.data))
      .catch((e) => { setClients([]); setListError(getErrorMessage(e)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const createClient = async () => {
    if (!form.name.trim()) { setFormError("Nama klien wajib diisi."); return; }
    setSaving(true); setFormError(null);
    try {
      await api.post("/clients", form);
      toast.success("Klien dibuat");
      setOpen(false); setForm(blankClient);
      await load();
    } catch (e) {
      // Without this the request simply vanished and the button looked broken (B-400).
      setFormError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const validateEngagement = () => {
    if (!eng.client_id) return "Pilih klien terlebih dahulu.";
    if (!eng.name.trim()) return "Nama perikatan wajib diisi.";
    if (!String(eng.period).trim()) return "Periode wajib diisi.";
    if (!Number.isInteger(eng.year) || eng.year < 2000 || eng.year > THIS_YEAR + 5)
      return `Tahun harus antara 2000 dan ${THIS_YEAR + 5}.`;
    return null;
  };

  const createEng = async () => {
    const invalid = validateEngagement();
    if (invalid) { setFormError(invalid); return; }
    if (saving) return;
    setSaving(true); setFormError(null);
    try {
      const { data } = await api.post("/engagements", eng);
      if (!data?.id) throw new Error("Server tidak mengembalikan id perikatan.");
      toast.success("Perikatan dibuat");
      setEngOpen(false);
      await load();
      // Navigate only once the engagement is known to exist and its id is in hand.
      navigate(`/engagements/${data.id}`);
    } catch (e) {
      // The modal stays open so the form is not lost and the reason is on screen.
      setFormError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const openEngagements = async (client) => {
    setOpenPicker(client); setEngagements([]); setFormError(null);
    try {
      const { data } = await api.get("/engagements/picker");
      const mine = (data || []).filter((e) => e.client_id === client.id);
      if (mine.length === 1) { setOpenPicker(null); navigate(`/engagements/${mine[0].id}`); return; }
      setEngagements(mine);
    } catch (e) {
      setFormError(getErrorMessage(e));
    }
  };

  const startNewEngagement = (client) => {
    setEng({ ...blankEngagement(THIS_YEAR), client_id: client.id,
             name: `Audit Laporan Keuangan ${THIS_YEAR}` });
    setFormError(null);
    setEngOpen(true);
  };

  const errorBox = formError && (
    <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="form-error">
      <TriangleAlert size={15} className="mt-0.5 shrink-0" /><span>{formError}</span>
    </div>
  );

  return (
    <Layout>
      <PageHeader title="Klien" subtitle="Daftar klien KAP" breadcrumb="Audit OS · Klien"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setFormError(null); }}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-client-btn"><Plus size={15} className="mr-1" />Klien Baru</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Klien Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {errorBox}
                <div><Label>Nama Klien</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Industri</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
                  <div><Label>NPWP</Label><Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nama PIC</Label><Input value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} /></div>
                  <div><Label>Email PIC</Label><Input value={form.pic_email} onChange={(e) => setForm({ ...form, pic_email: e.target.value })} /></div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.is_listed} onCheckedChange={(v) => setForm({ ...form, is_listed: !!v })} />Perusahaan Terbuka (PIE)</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.is_group} onCheckedChange={(v) => setForm({ ...form, is_group: !!v })} />Grup / Konsolidasi</label>
                </div>
              </div>
              <DialogFooter><Button onClick={createClient} disabled={saving} data-testid="save-client">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />
      <div className="p-6">
        {clients === null && !listError && <div className="text-sm text-muted-foreground" data-testid="clients-loading">Memuat...</div>}
        {listError && (
          <div className="bg-white border border-red-300 rounded-sm p-5 text-sm mb-4" data-testid="clients-error">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-1"><TriangleAlert size={16} /> Daftar klien tidak dapat dimuat</div>
            <div className="text-muted-foreground mb-3">{listError}</div>
            <Button variant="outline" size="sm" onClick={load}>Coba lagi</Button>
          </div>
        )}
        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          <table className="w-full audit-table">
            <thead><tr><th>Nama</th><th>Industri</th><th>NPWP</th><th>Tipe</th><th className="text-right">Perikatan</th><th></th></tr></thead>
            <tbody>
              {(clients || []).map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td className="font-medium">{c.name}{c.is_listed && <span className="ml-2 text-[10px] px-1 border rounded-sm bg-muted">PIE</span>}</td>
                  <td>{c.industry}</td>
                  <td className="font-mono text-xs">{c.npwp}</td>
                  <td>{c.is_group ? "Grup" : "Entitas Tunggal"}</td>
                  <td className="text-right tabular">{c.engagement_count}</td>
                  <td className="text-right whitespace-nowrap">
                    {/* Opening existing work and starting new work are different intentions, so
                        they are different buttons. Before, only "+ Perikatan" existed, which
                        invited creating a duplicate just to reach an engagement. */}
                    {c.engagement_count > 0 && (
                      <Button variant="outline" size="sm" className="mr-2" data-testid={`open-eng-${c.id}`}
                        onClick={() => openEngagements(c)}>
                        <FolderOpen size={14} className="mr-1" />Buka Perikatan
                      </Button>
                    )}
                    <Button variant="outline" size="sm" data-testid={`new-eng-${c.id}`}
                      onClick={() => startNewEngagement(c)}>
                      + Perikatan
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!openPicker} onOpenChange={(v) => { if (!v) setOpenPicker(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buka Perikatan</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="open-eng-list">
            {errorBox}
            {!formError && engagements.length === 0 && <div className="text-sm text-muted-foreground">Memuat...</div>}
            {engagements.map((e) => (
              <button key={e.id} data-testid={`open-eng-row-${e.id}`}
                onClick={() => { setOpenPicker(null); navigate(`/engagements/${e.id}`); }}
                className="w-full text-left border border-border rounded-sm px-3 py-2 hover:border-slate-800">
                <div className="text-sm font-medium">{e.name}</div>
                <div className="text-[11px] text-muted-foreground">{[e.period, e.phase].filter(Boolean).join(" · ")}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={engOpen} onOpenChange={(v) => { setEngOpen(v); if (!v) setFormError(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Perikatan Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {errorBox}
            <div><Label>Nama Perikatan</Label><Input value={eng.name} onChange={(e) => setEng({ ...eng, name: e.target.value })} data-testid="eng-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Periode</Label><Input value={eng.period} onChange={(e) => setEng({ ...eng, period: e.target.value })} data-testid="eng-period" /></div>
              <div><Label>Tahun</Label><Input type="number" value={eng.year} data-testid="eng-year"
                onChange={(e) => setEng({ ...eng, year: parseInt(e.target.value, 10) })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kerangka Pelaporan</Label><Input value={eng.framework} onChange={(e) => setEng({ ...eng, framework: e.target.value })} data-testid="eng-framework" /></div>
              <div><Label>Standar Audit</Label><Input value={eng.applicable_standards} onChange={(e) => setEng({ ...eng, applicable_standards: e.target.value })} data-testid="eng-standards" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jenis Perikatan</Label><Input value={eng.engagement_type} onChange={(e) => setEng({ ...eng, engagement_type: e.target.value })} data-testid="eng-type" /></div>
              <div><Label>Versi Metodologi</Label><Input value={eng.methodology_version} onChange={(e) => setEng({ ...eng, methodology_version: e.target.value })} data-testid="eng-methodology" /></div>
            </div>
            {/* Shown as editable defaults rather than filled in silently, so the auditor can see
                what the engagement is being pinned to. */}
            <div className="text-[11px] text-muted-foreground">Nilai di atas adalah default firma dan dapat diubah sebelum perikatan dibuat. Versi metodologi dikunci ke perikatan setelah dibuat.</div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={eng.eqr_required} data-testid="eng-eqr" onCheckedChange={(v) => setEng({ ...eng, eqr_required: !!v })} />Wajib EQR (Reviu Mutu Perikatan)</label>
          </div>
          <DialogFooter>
            <Button onClick={createEng} disabled={saving} data-testid="save-eng">{saving ? "Membuat..." : "Buat & Buka"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
